"use client";
import React, { useState } from "react";
import Head from "next/head";
import PageInformationCard from "~/components/PageInformationCard.jsx";
import { TurnrText } from "~/components/TurnrText.jsx";
import TurnrLink from "~/components/links/TurnrLink.jsx";
import { ENTITY_SCOPE, ENTITY_TYPE } from "~/constants.jsx";
import { getReturnReasonsPageData, getShopSettingsHref } from "~/cores/pageAccessCore.jsx";
import EntityListCards from "~/components/entityAction/EntityListCards.js";
import { getDefaultLanguage, getSelectedBrandEnabledLanguagesIncludingEnglishAISorted, getSelectedBrandId } from "~/cores/appCore.js";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import LanguageSelect from "~/components/input/selects/LanguageSelect.jsx";
import ReturnReasonOverviewRow from "~/app/(pages)/settings/return-reasons/ReturnReasonsOverviewRow.jsx";
import EntityList from "~/components/entityAction/EntityList.js";
import { COLORS } from "rylak-common/src/constants.js";
import ReturnReasonConfigOverviewCardContent from "~/app/(pages)/settings/return-reasons/ReturnReasonsConfigOverviewCardContent.jsx";

export default function ReturnReasonsPage({ appProps }) {
    const [selectedLanguage, setSelectedLanguage] = useState(getDefaultLanguage(appProps));

    const { getReturnReasonsServiceData, clearReturnReasonsServiceData, getComplaintReasonsServiceData, clearComplaintReasonsServiceData } = appProps;

    const returnReasonsServiceData = getReturnReasonsServiceData();
    const complaintReasonsServiceData = getComplaintReasonsServiceData();

    function onUpdate() {
        clearReturnReasonsServiceData();
        clearComplaintReasonsServiceData();
    }

    return (
        <>
            <Head>
                <title>{getReturnReasonsPageData().title}</title>
            </Head>

            <PageInformationCard
                title={getReturnReasonsPageData().title}
                content={
                    <div>
                        <TurnrText size={"sm"}>
                            You can offer different return and complaint reasons to your customers depending on what type of product they are are returning.
                            <br />
                            <br />
                            If a returned item does not match any of your custom Return Reason Setups then they will be offered the return reason setup without any pre-conditions.
                            <br />
                            <br />
                            If you are missing translations for any language then those will be automatically translated from english.
                        </TurnrText>

                        <div style={{ paddingTop: "30px" }}>
                            <TurnrGrid
                                style={{
                                    gridTemplateColumns: "auto 1fr",
                                    gridGap: "20px",
                                }}
                            >
                                <LanguageSelect
                                    size={"small"}
                                    label={"Preview"}
                                    tooltip={"This language selector only changes the language you're currently viewing. It doesn't modify any settings or content."}
                                    style={{ minWidth: "220px" }}
                                    appProps={appProps}
                                    value={selectedLanguage}
                                    languageCodes={getSelectedBrandEnabledLanguagesIncludingEnglishAISorted(appProps)}
                                    onChange={setSelectedLanguage}
                                />
                            </TurnrGrid>
                            <div className={"mt1"}>
                                <TurnrLink href={getShopSettingsHref()}>Enable more languages</TurnrLink>
                            </div>
                        </div>
                    </div>
                }
            />
            <TurnrGrid container spacing={2}>
                <TurnrGrid item xs={12} lg={8} xl={7}>
                    <EntityListCards
                        appProps={appProps}
                        entityType={ENTITY_TYPE.RETURN_REASON_CONFIG}
                        fetchEntitiesQueryParams={{ shopId: getSelectedBrandId(appProps), entityScope: ENTITY_SCOPE.FULL, sort: "priority" }}
                        onUpdate={onUpdate}
                        EntityContentComponent={ReturnReasonConfigOverviewCardContent}
                        entityContentComponentProps={{ selectedLanguage }}
                        initialLanguageCode={selectedLanguage}
                    />
                </TurnrGrid>
                <TurnrGrid item xs={12} lg={4} xl={5}>
                    <TurnrGrid container item spacing={1}>
                        <TurnrGrid item xs={12} sm={6} lg={12} xl={6}>
                            <EntityList
                                appProps={appProps}
                                entityType={ENTITY_TYPE.RETURN_REASON}
                                entitiesServiceData={returnReasonsServiceData}
                                title={"All return reasons"}
                                description={
                                    <div>
                                        <div style={{ fontSize: "12px", color: COLORS.SLIGHTLY_WEAK_TEXT }}>Create, edit and view return reasons to be used by the setups.</div>
                                    </div>
                                }
                                tightRows={true}
                                EntityContentComponent={ReturnReasonOverviewRow}
                                entityContentComponentProps={{ selectedLanguage }}
                                initialLanguageCode={selectedLanguage}
                                onUpdate={onUpdate}
                            />
                        </TurnrGrid>
                        <TurnrGrid item xs={12} sm={6} lg={12} xl={6}>
                            <EntityList
                                appProps={appProps}
                                entityType={ENTITY_TYPE.COMPLAINT_REASON}
                                entitiesServiceData={complaintReasonsServiceData}
                                title={"All complaint reasons"}
                                description={
                                    <div>
                                        <div style={{ fontSize: "12px", color: COLORS.SLIGHTLY_WEAK_TEXT }}>Create, edit and view complaint reasons to be used by the setups.</div>
                                    </div>
                                }
                                tightRows={true}
                                EntityContentComponent={ReturnReasonOverviewRow}
                                entityContentComponentProps={{ selectedLanguage }}
                                initialLanguageCode={selectedLanguage}
                                onUpdate={onUpdate}
                            />
                        </TurnrGrid>
                    </TurnrGrid>
                </TurnrGrid>
            </TurnrGrid>
        </>
    );
}
